import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {Trans} from '@common/i18n/trans';
import {Button} from '@common/ui/buttons/button';
import {apiClient} from '@common/http/query-client';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';

interface AccountVkSettings {
  id: number;
  user_id: number;
  link_code: string;
  vk_user_id: string;
  vk_peer_id: string | null;
  is_connected: boolean;
  connected_at: string | null;
}

/**
 * Изменено: 2026-04-28 — панель привязки VK в настройках аккаунта.
 * Изменено: 2026-04-28 — добавлена кнопка отправки тестового сообщения в VK.
 * Изменено: 2026-04-28 — для подключенного VK показываем кнопку отключения вместо генерации кода.
 * Функциональность: показывает код для сообщения в VK-группу и позволяет регенерировать код.
 */
export function VkConnectionPanel() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['account-vk-settings'],
    queryFn: async () => {
      const response = await apiClient.get<{settings: AccountVkSettings}>(
        'account/vk-settings',
      );
      return response.data.settings;
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.put<{settings: AccountVkSettings}>(
        'account/vk-settings',
        {regenerate_link_code: true},
      );
      return response.data.settings;
    },
    onSuccess: settings => {
      queryClient.setQueryData(['account-vk-settings'], settings);
      toast(message('VK link code updated'));
    },
    onError: error => showHttpErrorToast(error),
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.put<{settings: AccountVkSettings}>(
        'account/vk-settings',
        {disconnect: true},
      );
      return response.data.settings;
    },
    onSuccess: settings => {
      queryClient.setQueryData(['account-vk-settings'], settings);
      toast(message('VK disconnected'));
    },
    onError: error => showHttpErrorToast(error),
  });

  const testMessageMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('account/vk-settings/test-message');
    },
    onSuccess: () => {
      toast(message('VK test message sent'));
    },
    onError: error => showHttpErrorToast(error),
  });

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.VkConnection}
      title={<Trans message="VK group connection" />}
    >
      <div className="space-y-12">
        <div className="text-muted text-sm">
          <Trans message="To connect VK, go to the community page, subscribe, click the button and send this code in chat." />
        </div>
        <a
          href="https://vk.com/club238181622"
          target="_blank"
          rel="noreferrer"
          className="inline-block text-sm text-primary hover:underline"
        >
          <Trans message="Open VK community: vk.com/club238181622" />
        </a>
        {!settingsQuery.data?.is_connected ? (
          <div className="rounded bg-chip p-10 font-mono text-sm">
            {settingsQuery.data?.link_code || '—'}
          </div>
        ) : null}
        {settingsQuery.data?.is_connected ? (
          <div className="text-sm text-positive">
            <Trans message="Linked successfully" />
          </div>
        ) : null}
        <div className="flex items-center gap-8">
          {settingsQuery.data?.is_connected ? (
            <Button
              variant="outline"
              size="sm"
              disabled={testMessageMutation.isPending}
              onClick={() => testMessageMutation.mutate()}
            >
              <Trans message="Send test VK message" />
            </Button>
          ) : (
            <Button
              variant="flat"
              size="sm"
              disabled={regenerateMutation.isPending}
              onClick={() => regenerateMutation.mutate()}
            >
              <Trans message="Generate new VK link code" />
            </Button>
          )}
          {settingsQuery.data?.is_connected ? (
            <Button
              variant="flat"
              size="sm"
              color="danger"
              disabled={disconnectMutation.isPending}
              onClick={() => disconnectMutation.mutate()}
            >
              <Trans message="Disconnect VK" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled
            >
              <Trans message="Send test VK message" />
            </Button>
          )}
        </div>
      </div>
    </AccountSettingsPanel>
  );
}
